import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Calendar, Clock, Plus, Edit, Trash2, Settings, RefreshCw } from 'lucide-react';
import api from '../../lib/api';

const SCHEDULE_TYPES = [
  { value: 'rent_collection', label: 'Rent Collection', color: 'bg-blue-500' },
  { value: 'landlord_payout', label: 'Landlord Payout', color: 'bg-green-500' },
  { value: 'contractor_payout', label: 'Contractor Payout', color: 'bg-orange-500' },
  { value: 'deposit_lodgement', label: 'Deposit Lodgement', color: 'bg-purple-500' },
  { value: 'deposit_return', label: 'Deposit Return', color: 'bg-pink-500' },
];

const FREQUENCIES = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
];

export default function FinanceSchedules() {
  const { user } = useAuth();
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [formData, setFormData] = useState({
    schedule_type: 'rent_collection',
    name: '',
    description: '',
    frequency: 'monthly',
    day_of_month: 1,
    skip_weekends: true,
    skip_bank_holidays: true,
    retry_attempts: 3,
  });

  useEffect(() => {
    fetchSchedules();
  }, []);

  const fetchSchedules = async () => {
    try {
      const response = await api.get('/admin/finance/schedules');
      setSchedules(response.data);
    } catch (error) {
      console.error('Error fetching schedules:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSchedule) {
        await api.put(`/admin/finance/schedules/${editingSchedule.id}`, formData);
      } else {
        await api.post('/admin/finance/schedules', formData);
      }
      setDialogOpen(false);
      setEditingSchedule(null);
      resetForm();
      fetchSchedules();
    } catch (error) {
      console.error('Error saving schedule:', error);
    }
  };

  const handleEdit = (schedule) => {
    setEditingSchedule(schedule);
    setFormData({
      schedule_type: schedule.schedule_type,
      name: schedule.name,
      description: schedule.description || '',
      frequency: schedule.frequency,
      day_of_month: schedule.day_of_month || 1,
      skip_weekends: schedule.skip_weekends,
      skip_bank_holidays: schedule.skip_bank_holidays,
      retry_attempts: schedule.retry_attempts,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to deactivate this schedule?')) {
      try {
        await api.delete(`/admin/finance/schedules/${id}`);
        fetchSchedules();
      } catch (error) {
        console.error('Error deleting schedule:', error);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      schedule_type: 'rent_collection',
      name: '',
      description: '',
      frequency: 'monthly',
      day_of_month: 1,
      skip_weekends: true,
      skip_bank_holidays: true,
      retry_attempts: 3,
    });
  };

  const getScheduleTypeInfo = (type) => {
    return SCHEDULE_TYPES.find(t => t.value === type) || { label: type, color: 'bg-gray-500' };
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Finance Schedules</h1>
            <p className="text-gray-500">Configure automated payment schedules for collections and payouts</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) {
              setEditingSchedule(null);
              resetForm();
            }
          }}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                New Schedule
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingSchedule ? 'Edit Schedule' : 'Create Schedule'}</DialogTitle>
                <DialogDescription>
                  Configure the schedule settings for automated financial processing.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Schedule Type</Label>
                  <Select value={formData.schedule_type} onValueChange={(v) => setFormData({...formData, schedule_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SCHEDULE_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="e.g., Monthly Rent Collection"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Optional description"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Frequency</Label>
                    <Select value={formData.frequency} onValueChange={(v) => setFormData({...formData, frequency: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {FREQUENCIES.map(f => (
                          <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Day of Month</Label>
                    <Input
                      type="number"
                      min="1"
                      max="28"
                      value={formData.day_of_month}
                      onChange={(e) => setFormData({...formData, day_of_month: parseInt(e.target.value)})}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.skip_weekends}
                      onChange={(e) => setFormData({...formData, skip_weekends: e.target.checked})}
                      className="rounded"
                    />
                    Skip Weekends
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.skip_bank_holidays}
                      onChange={(e) => setFormData({...formData, skip_bank_holidays: e.target.checked})}
                      className="rounded"
                    />
                    Skip Bank Holidays
                  </label>
                </div>
                <div className="space-y-2">
                  <Label>Retry Attempts</Label>
                  <Input
                    type="number"
                    min="0"
                    max="10"
                    value={formData.retry_attempts}
                    onChange={(e) => setFormData({...formData, retry_attempts: parseInt(e.target.value)})}
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit">{editingSchedule ? 'Update' : 'Create'}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Schedule Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {schedules.map(schedule => {
            const typeInfo = getScheduleTypeInfo(schedule.schedule_type);
            return (
              <Card key={schedule.id} className={!schedule.is_active ? 'opacity-50' : ''}>
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <Badge className={`${typeInfo.color} text-white mb-2`}>
                        {typeInfo.label}
                      </Badge>
                      <CardTitle className="text-lg">{schedule.name}</CardTitle>
                    </div>
                    {schedule.is_platform_default && (
                      <Badge variant="outline">Platform Default</Badge>
                    )}
                  </div>
                  {schedule.description && (
                    <CardDescription>{schedule.description}</CardDescription>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Calendar className="w-4 h-4" />
                      <span className="capitalize">{schedule.frequency}</span>
                      {schedule.day_of_month && (
                        <span>on day {schedule.day_of_month}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Clock className="w-4 h-4" />
                      <span>{schedule.retry_attempts} retry attempts</span>
                    </div>
                    <div className="flex gap-2 text-xs">
                      {schedule.skip_weekends && <Badge variant="secondary">Skip Weekends</Badge>}
                      {schedule.skip_bank_holidays && <Badge variant="secondary">Skip Holidays</Badge>}
                    </div>
                  </div>
                  {!schedule.is_platform_default && (
                    <div className="flex gap-2 mt-4 pt-4 border-t">
                      <Button size="sm" variant="outline" onClick={() => handleEdit(schedule)}>
                        <Edit className="w-4 h-4 mr-1" /> Edit
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => handleDelete(schedule.id)}>
                        <Trash2 className="w-4 h-4 mr-1" /> Delete
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {schedules.length === 0 && (
          <Card>
            <CardContent className="text-center py-12">
              <Settings className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <h3 className="font-medium text-lg">No Schedules Configured</h3>
              <p className="text-gray-500 mt-2">Create your first schedule to automate financial processing.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
